/** Panoul din dreapta: ce e selectat, cu parametrii lui.
 *
 *  AȘEZAT CA ÎN HOMESTYLER, fiindcă e forma pe care oricine a desenat un plan o
 *  recunoaște: titlul spune CE e selectat, dedesubt «Parametri» cu mărimile, apoi
 *  «Atribute» cu felurile. Fără selecție, panoul arată parametrii MODULULUI —
 *  altfel jumătate din ecran ar sta goală tocmai când te uiți prima oară la el.
 *
 *  DEOCAMDATĂ CITEȘTE, NU SCRIE. Câmpurile sunt cele pe care editorul le va face
 *  editabile, la locul lor, cu unitățile lor — dar calea de scriere nu există
 *  încă: o editare înseamnă recalcularea a tot ce e derivat în document (ariile
 *  pe cameră, `useful_m2` pe variantă), re-derivarea lui `alpha` și cele ~15
 *  probe din `layout/tronsoane.py`. Un câmp care se lasă schimbat și nu salvează
 *  e mai rău decât unul care se vede că nu se schimbă încă — v.
 *  `components/rol.tsx`, care spune același lucru despre butoane.
 *
 *  ÎNĂLȚIMEA ARE DOUĂ STĂRI, ȘI PANOUL LE ARATĂ PE AMÂNDOUĂ. Un perete care nu-și
 *  declară înălțimea merge de la pardoseală la placă, deci scrie 2,80 fiindcă
 *  atâta e nivelul; unul care și-o declară scrie 1,05 fiindcă așa e el. Aceeași
 *  cifră ar putea veni din amândouă, iar deosebirea contează: primul urmează
 *  nivelul când se schimbă, al doilea nu.
 */
import { numeleFelului } from "./feluri";
import Etichete from "./Etichete";
import { numeStrat } from "./design/straturi";
import { useT, useTn } from "../../i18n";
import type { Cheie } from "../../i18n";
import { modelUsa, type Camera, type Perete, type PlanTronson, type TronsonDoc } from "./doc";
import type { Ales } from "./Desen";
import type { Eticheta } from "../../api";

/** Numele pe care le poartă felurile de perete în interfață. `parapet` e
 *  singurul care nu merge până la placă — de-aia își declară înălțimea. */
const FEL_PERETE: Record<string, Cheie> = {
  structural: "prop.perete.structural",
  partition: "prop.perete.partition",
  facade: "prop.perete.facade",
  fire: "prop.perete.fire",
  parapet: "prop.perete.parapet",
};

const MATERIAL: Record<string, Cheie> = {
  beton: "prop.material.beton",
  zidarie: "prop.material.zidarie",
  polistiren: "prop.material.polistiren",
  gips_carton: "prop.material.gips_carton",
  sticla: "prop.material.sticla",
};

function Rand({ eticheta, children, nota }: {
  eticheta: string; children: React.ReactNode; nota?: string;
}) {
  return (
    <div className="ob-tr-rand">
      <span className="ob-tr-et">{eticheta}</span>
      <span className="ob-tr-val">{children}</span>
      {nota && <span className="ob-tr-nota">{nota}</span>}
    </div>
  );
}

function Metri({ v }: { v: number }) {
  return <>{v.toFixed(3)} <span className="ob-tr-um">m</span></>;
}

function Sectiune({ titlu, children }: {
  titlu: string; children: React.ReactNode;
}) {
  return (
    <div className="ob-tr-sect">
      <div className="ob-lbl">{titlu}</div>
      {children}
    </div>
  );
}

function PereteSelectat({ w, nivel }: { w: Perete; nivel: number }) {
  const t = useT();
  const urmeaza = w.proprie == null;
  return (
    <>
      <Sectiune titlu={t("citire.parametri")}>
        <Rand eticheta={t("prop.grosime")}><Metri v={w.grosime} /></Rand>
        <Rand eticheta={t("prop.lungime")}><Metri v={w.lungime} /></Rand>
        <Rand eticheta={t("prop.inaltime")}
              nota={urmeaza
                ? t("citire.inaltime_urmeaza")
                : t("citire.inaltime_declarata", { n: nivel.toFixed(2) })}>
          <Metri v={w.inaltime} />
        </Rand>
      </Sectiune>
      <Sectiune titlu={t("prop.sect.atribute")}>
        <Rand eticheta={t("prop.tip_perete")}>
          {FEL_PERETE[w.kind] ? t(FEL_PERETE[w.kind]) : w.kind}
        </Rand>
        <Rand eticheta={t("prop.material")}>
          {MATERIAL[w.material] ? t(MATERIAL[w.material]) : w.material}
        </Rand>
        {/* PORTANT SE CITEȘTE DIN MATERIAL, nu se declară separat. Regula e a
            catalogului: «betonul e întotdeauna `structural` — el duce
            încărcarea la fundație, deci el se propagă în subsol». Un al doilea
            steag ar putea contrazice materialul. */}
        <Rand eticheta={t("citire.portant")}>
          {t(w.material === "beton" ? "citire.portant_da" : "citire.portant_nu")}
        </Rand>
        {w.peFatada && (
          <Rand eticheta={t("prop.sect.pozitie")}>{t("prop.pe_fatada")}</Rand>
        )}
        {w.inchideNucleu && (
          <Rand eticheta={t("prop.sect.pozitie")}>{t("prop.inchide_nucleul")}</Rand>
        )}
      </Sectiune>
      {/* ALCĂTUIREA, când zidul o spune. Se CITEȘTE aici, în aceeași ordine ca
          în editor — de la stânga la dreapta a peretelui —, fiindcă întrebarea
          «din ce e făcut zidul ăsta» se pune la fel de des privind un modul cât
          lucrând pe el, iar răspunsul n-ar trebui să ceară deschiderea
          editorului. */}
      {w.straturi.length > 0 && (
        <Sectiune titlu={t("prop.sect.straturi")}>
          {w.straturi.map((st, k) => (
            <Rand key={k} eticheta={numeStrat(st.kind)}>
              <Metri v={st.grosime} />
            </Rand>
          ))}
          <p className="ob-tr-nota">{t("prop.straturi.nota")}</p>
        </Sectiune>
      )}
    </>
  );
}

function CameraSelectata({ c }: { c: Camera }) {
  const t = useT();
  return (
    <>
      <Sectiune titlu={t("citire.parametri")}>
        {c.arie != null && (
          <Rand eticheta={t("prop.arie")}>
            {c.arie.toFixed(2)} <span className="ob-tr-um">m²</span>
          </Rand>
        )}
      </Sectiune>
      <Sectiune titlu={t("prop.sect.atribute")}>
        <Rand eticheta={t("prop.fel")}>{numeleFelului(c.kind)}</Rand>
        <Rand eticheta={t("prop.latura")}>{c.side}</Rand>
        {c.flat != null && (
          <Rand eticheta={t("citire.apartament")}>{c.flat + 1}</Rand>
        )}
      </Sectiune>
    </>
  );
}

/** Un gol. Lipsea, iar consecința nu arăta ca o lipsă: selectarea unei uși
 *  cădea pe ramura din urmă și panoul scria parametrii MODULULUI, deci părea că
 *  ușile nu se pot alege. Verificat pe M8, unde toate cele zece se selectau
 *  fără ca ceva pe ecran s-o arate. */
function GolSelectat({ g, fel, usa }: {
  g: { w_m?: number; h_m?: number; parapet_m?: number; sill_m?: number | null;
       wall?: string | null; model?: string };
  fel: "doors" | "windows";
  /** Ușa derivată, când golul e ușă: rolul și tocul ei sunt citiri, nu câmpuri. */
  usa: { intrare: boolean; toc: number } | null;
}) {
  const t = useT();
  return (
    <Sectiune titlu={t("citire.parametri")}>
      {fel === "doors" && usa && (
        <>
          <Rand eticheta={t("editor.gol_model")}>{t(`fel.usa.${modelUsa(g.model)}`)}</Rand>
          <Rand eticheta={t("editor.gol_rol")}>
            {usa.intrare ? t("editor.gol_intrare") : t("editor.gol_interioara")}
          </Rand>
          <Rand eticheta={t("editor.gol_toc")}><Metri v={usa.toc} /></Rand>
        </>
      )}
      <Rand eticheta={t("prop.latime")}><Metri v={g.w_m ?? 0.9} /></Rand>
      <Rand eticheta={t("prop.inaltime")}><Metri v={g.h_m ?? 2.1} /></Rand>
      {fel === "windows" && (
        <Rand eticheta={t("prop.parapet")}>
          <Metri v={g.parapet_m ?? g.sill_m ?? 0.9} />
        </Rand>
      )}
      <Rand eticheta={t("prop.pe_peretele")}>
        <span className="ob-ref">{g.wall ?? "—"}</span>
      </Rand>
    </Sectiune>
  );
}

function Modulul({ doc, plan }: { doc: TronsonDoc; plan: PlanTronson }) {
  const t = useT();
  const tn = useTn();
  const f = doc.front;
  return (
    <>
      <Sectiune titlu={t("citire.parametri")}>
        <Rand eticheta={t("prop.front_nominal")}><Metri v={f.nominal_m} /></Rand>
        <Rand eticheta={t("citire.front_minim")}
              nota={f.rigid ? t("citire.front_rigid") : undefined}>
          <Metri v={f.min_m} />
        </Rand>
        <Rand eticheta={t("citire.front_maxim")}><Metri v={f.max_m} /></Rand>
        <Rand eticheta={t("prop.banda")}><Metri v={doc.band_depth_m} /></Rand>
        <Rand eticheta={t("prop.inaltime_nivel")}
              nota={doc.storey_height_m == null ? t("citire.implicit") : undefined}>
          <Metri v={plan.nivel} />
        </Rand>
      </Sectiune>
      <Sectiune titlu={t("prop.sect.alcatuire")}>
        <Rand eticheta={t("prop.camere")}>{plan.camere.length}</Rand>
        <Rand eticheta={t("prop.pereti")}>{plan.pereti.length}</Rand>
        <Rand eticheta={t("prop.goluri")}>{plan.goluri.length}</Rand>
        {doc.core && (
          <Rand eticheta={t("citire.nucleu")}>
            {tn("citire.nucleu_lifturi#n", doc.core.lifts ?? 1)}
          </Rand>
        )}
      </Sectiune>
    </>
  );
}

export default function PanouProprietati({
  doc, plan, ales, etichete = [], posibileEtichete = [], laEtichete,
  scriuEtichete,
}: {
  doc: TronsonDoc; plan: PlanTronson; ales: Ales;
  /** ETICHETELE SUNT SINGURUL LUCRU CARE SE SCRIE DIN PANOUL ĂSTA, și nu e o
   *  excepție de la nota de sus: ele nu sunt în document. Nu cer recalcularea
   *  nimic — nici arie, nici `alpha`, nici amprentă —, nu ramifică modulul și
   *  se scriu pe loc, cu ruta lor. Restul câmpurilor rămân de citit până când
   *  calea de scriere a documentului ajunge și aici. */
  etichete?: string[];
  posibileEtichete?: Eticheta[];
  /** Lipsă = drept de citire, sau ecranul nu le poate scrie. */
  laEtichete?: (etichete: string[]) => void;
  scriuEtichete?: boolean;
}) {
  const t = useT();
  const perete = ales?.fel === "perete" ? plan.pereti[ales.i] : null;
  const camera = ales?.fel === "camera" ? plan.camere[ales.i] : null;
  const gol = ales?.fel === "gol" ? (doc.plan?.[ales.fel2] ?? [])[ales.i] : null;
  const titlu = perete
    ? `${FEL_PERETE[perete.kind] ? t(FEL_PERETE[perete.kind]) : perete.kind}`
      + ` · ${perete.id}`
    : camera
      ? numeleFelului(camera.kind)
      : gol && ales?.fel === "gol"
        ? t(ales.fel2 === "doors" ? "prop.titlu.usa" : "prop.titlu.fereastra")
        : `${doc.id} — ${doc.label ?? ""}`;

  return (
    <aside className="ob-tr-panou">
      <div className="ob-tr-titlu">{titlu}</div>
      {/* ETICHETELE, PRIMUL LUCRU SUB TITLU, și numai când nu e nimic ales:
          atunci panoul vorbește despre MODUL, iar etichetele sunt ale
          modulului, nu ale peretelui pe care tocmai ai apăsat. */}
      {!ales && (
        <div className="ob-tr-sect ob-tr-etichete">
          <div className="ob-lbl">{t("etichete.titlu")}</div>
          <Etichete etichete={etichete} posibile={posibileEtichete}
                    laSchimbare={laEtichete} lucrez={scriuEtichete} />
        </div>
      )}
      {perete ? <PereteSelectat w={perete} nivel={plan.nivel} />
        : camera ? <CameraSelectata c={camera} />
          : gol && ales?.fel === "gol"
            ? <GolSelectat g={gol} fel={ales.fel2}
                           usa={ales.fel2 === "doors"
                             ? (plan.goluri.find((x) => x.fel === "usa" && x.idx === ales.i) ?? null)
                             : null} />
            : <Modulul doc={doc} plan={plan} />}
      <p className="ob-tr-citire">
        {t(ales ? "citire.se_citeste" : "citire.apasa")}
      </p>
    </aside>
  );
}
